// Style
import mainStyle from "../mainStyle.module.scss";

// Packages
import React, { useState, useEffect, useMemo } from "react";
import { isEqual } from "lodash";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Button,
  Typography,
  Tooltip,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Components
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import { StyledBox } from "../components/StyledBox";

// Hook
import useDataSourceManager from "hooks/useDataSourceManager";

// Icons
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as AlertIcon } from "assets/images/icons/icn_board_alert.svg";
import { ReactComponent as CheckedIcon } from "assets/images/icons/icn_checked.svg";
import { ReactComponent as UnCheckedIcon } from "assets/images/icons/icn_unchecked.svg";
import { ReactComponent as CheckChangedIcon } from "assets/images/icons/icn_check_changed.svg";

// Global const
import {
  formLimitation,
} from "const";

// Parent const
import {
  defaultActions,
  editUuids,
  defaultPortTypes,
  defaultStateTypes,
} from "../const";

// Global utils
import {
  calcVlanIdsCountByDevice,
} from "utils/validate";

// Parent utils
import {
  getPortType,
  getProfileRequestParams,
  getInitProfileList,
  checkAllowVlans,
  checkPvid,
  getInputStyles,
  getIntersectionLinkSpeeds,
} from "../helper";

const EditModalSwitch = ({
  auth,
  alertInfo,
  setAlertInfo,
  defaultConfig,
  setDefaultConfig,
  actions,
  setActions,
  selectedRows,
  showEditGeneral,
}) => {
  const [editUuids] = useState(() => ({
    getProfileList: uuidv4()
  }));
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();

  const [isEdited, setIsEdited] = useState({});

  const selectedPorts = useMemo(() => selectedRows.map(row => row.original), [selectedRows]);
  const originalSelectedPort = useMemo(() => selectedPorts ? structuredClone(selectedPorts[0]) : {}, [selectedPorts]);
  const [selectedPort, setSelectedPort] = useState(originalSelectedPort ? structuredClone(originalSelectedPort) : {});
  const isNeedCombineModel = useMemo(() => !!selectedPort?.isNeedCombineModel, [selectedPort]);

  const [profileList, setProfileList] = useState([]);
  const selectedProfile = useMemo(() => profileList.find(profile => profile.isSelected), [profileList]);
  const isProfileMode = useMemo(() => !selectedProfile?.isDefault && !!selectedProfile?.value, [selectedProfile?.value]);

  const isEditable = useMemo(() => !selectedProfile || selectedProfile?.isDefault, [selectedProfile]);
  const extraCount = selectedRows.length > 1 ? selectedRows.length - 1 : 0;
  const { isCopper, isFiber } = useMemo(() => getPortType({ selectedPorts }), [selectedPorts]);

  const { copperList, fiberList } = useMemo(() => getIntersectionLinkSpeeds({ selectedPorts }), [selectedPorts]);
  const [linkSpeeds, setLinkSpeeds] = useState({
    copperList,
    fiberList
  });

  const vlanIdsCount = useMemo(() => calcVlanIdsCountByDevice(defaultConfig.portConfigs, selectedPort), [selectedPort]);
  const isVlanInvalid = useMemo(() => vlanIdsCount.number > 128 || selectedPort?.portType === defaultPortTypes.trunk.value && !checkAllowVlans(selectedPort?.allowVlan), [selectedPort?.portType, selectedPort?.allowVlan, vlanIdsCount]);

  const getProfileList = () => {
    const { apiUrl, requestParams } = getProfileRequestParams({ auth });

    sendRequest(
      apiUrl,
      editUuids.getProfileList,
      requestParams
    );
  };

  const onApply = () => {

    const { portConfigs } = defaultConfig;
    const portIndexMap = selectedPorts.reduce((acc, port) => {
      acc[port.portIndex] = port;
      return acc;
    }, {});

    const editedPortConfigs = portConfigs.map(port => {
      let resultPort = {};

      if (isNeedCombineModel || !!portIndexMap[port.portIndex]) {
        resultPort = {
          ...port,
          combinePortName: selectedPort.combinePortName,
          portName: isNeedCombineModel ? selectedPort.combinePortName : selectedPort.portName,
          portProfile: selectedPort.portProfile,
          portState: selectedPort.portState,
          portType: selectedPort.portType,
          PVID: selectedPort.PVID,
          allowVlan: selectedPort.allowVlan,
          speed: selectedPort.speed,
          comboSfpSpeed: selectedPort.comboSfpSpeed,
          poeState: selectedPort.poeState,
          ddpState: selectedPort.ddpState,
          lbdState: selectedPort.lbdState,
        }
      } else {
        resultPort = port;
      }

      return resultPort;
    })

    setDefaultConfig(prevConfig => {
      return {
        ...prevConfig,
        portConfigs: editedPortConfigs,
      }
    })

    setActions({ ...defaultActions, isOpenCreateTemplate: true, isOpenEditModal: false });
  };

  const onCancel = () => {
    setActions({ ...defaultActions, isOpenCreateTemplate: true, isOpenEditModal: false });
  };

  const updatePorts = (field, value) => {
    setSelectedPort((prev) => ({
      ...prev,
      [field]: value
    }));

    if (!isEqual(originalSelectedPort[field], value)) {
      setIsEdited((prev) => ({ ...prev, [field]: true }));
    } else {
      setIsEdited((prev) => {
        const { [field]: removedField, ...rest } = prev;
        return rest;
      });
    }
  };

  const isFormInvalid = () => {
    let isInvalid = false;
    const { portName, portType, PVID, allowVlan } = selectedPort;

    if (portName === "") isInvalid = true;
    if (!checkPvid(PVID?.toString().trim())) isInvalid = true;
    if (isVlanInvalid) isInvalid = true;

    return isInvalid;
  };

  useEffect(() => {
    if (actions?.isOpenEditModal && selectedRows.length > 0) {
      getProfileList();
      setIsEdited({});
    }
  }, [actions?.isOpenEditModal, selectedRows]);

  useEffect(() => {
    if (!selectedPort) return;

    const response = getData(editUuids.getProfileList);
    const responseData = response?.data ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to get the profile list.",
        });
      } else {
        setProfileList(getInitProfileList({
          profileList: responseData,
          selectedPort
        }));
      }
    }
  }, [dataMap?.[editUuids.getProfileList], selectedPort]);

  if (!selectedPorts?.length) return <></>;

  return (
    <>
      <ModalContainer display={actions?.isOpenEditModal} modalWidth="auto">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >

          <Typography
            sx={{
              mb: "30px",
              ml: "20px",
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Selected Ports
          </Typography>

          <Button
            onClick={onCancel}
            sx={{
              marginRight: "-10px",
              marginTop: "-28px",
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>

        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", ml: "20px", mt: "10px" }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              backgroundColor: theme.palette.primary.subBlue,
              borderRadius: theme.shape.customBorderRadius[0],
              gap: "10px",
              padding: "4px 8px",
            }}
          >
            {selectedPort?.seriesName ?? "No ports selected"}
          </Typography>

          {
            extraCount > 0 && (
              <Tooltip
                title={
                  <Box
                    className={mainStyle["custom-scrollbar"]}
                    sx={{
                      maxHeight: 250,
                      overflowY: "auto",
                      pr: 1,
                    }}
                  >
                    {
                      selectedPorts.map(port => {
                        return (
                          <Typography
                            sx={{
                              color: theme.palette.primary.white,
                              fontFamily: theme.typography.fontFamily,
                              fontSize: theme.typography.customFontSize[1],
                              fontWeight: theme.typography.customFontWeight[3],
                              lineHeight: 1.92,
                            }}
                            key={"seriesName_" + port.portIndex}
                          >
                            {port.formattedFullName}
                          </Typography>
                        );
                      })
                    }
                  </Box>
                }
                arrow
              >
                <Typography
                  sx={{
                    ml: 3,
                    color: theme.palette.primary.main,
                    fontSize: theme.typography.customFontSize[10],
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  +{extraCount} Selected
                </Typography>
              </Tooltip>
            )
          }
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", ml: "20px", mt: "10px" }}
        >
          <AlertIcon />
          <Box
            sx={{
              ml: "10px",
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Only changes highlighted in{" "}
            <Box
              component="span"
              sx={{
                color: theme.palette.primary.orange,
                fontWeight: "bold",
              }}
            >
              Orange
            </Box>{" "}
            will be applied to all selected ports.
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {selectedPort && (
            <>

              {/* Port Name */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Name
                </Typography>
                <TextField
                  type="text"
                  value={isNeedCombineModel ? selectedPort?.combinePortName : selectedPort?.portName}
                  disabled={!showEditGeneral}
                  onChange={e => {
                    if (isNeedCombineModel) updatePorts("combinePortName", e.target.value);
                    else updatePorts("portName", e.target.value);
                  }}
                  sx={getInputStyles({
                    theme,
                    isInValid: selectedPort?.portName === "",
                    isEdited,
                    field: "portName"
                  })}
                />
              </StyledBox>

              {/* Port Profile */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Profile
                </Typography>
                <MenuSelect
                  disabled={!showEditGeneral}
                  value={selectedProfile?.name ?? selectedProfile?.value ?? ""}
                  onChange={opt => {

                    setProfileList(prevProfileList => {
                      return prevProfileList.map(profile => {
                        return {
                          ...profile,
                          isSelected: profile.id === opt.id,
                        }
                      })
                    });

                    updatePorts("portProfile", opt.id);

                    // Sync the profile value or not
                    if (!opt.isDefault) {
                      // Non default profile
                      Object.keys(opt).forEach((key) => {
                        if (key !== "name" && key !== "deviceCategory" && key !== "portProfile") {
                          updatePorts(key, opt[key]);
                        }
                      });
                    } else {
                      // Default profile
                      Object.keys(originalSelectedPort).forEach((key) => {
                        if (key !== "name" && key !== "deviceCategory" && key !== "portProfile") {
                          updatePorts(key, originalSelectedPort[key]);
                        }
                      });
                    }

                  }}
                  options={profileList}
                  width="300px"
                  isEditedField={isEdited.portProfile}
                />
              </StyledBox>

              {/* Port State */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port State
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    row
                    disabled
                    value={selectedProfile?.portState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >

                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const portState = defaultStateTypes[key];
                        const changed = isEdited.portState != null;

                        return (
                          <FormControlLabel
                            disabled
                            key={portState.key}
                            value={portState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={portState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    disabled={!showEditGeneral}
                    value={selectedPort?.portState ? "enable" : "disable"}
                    onChange={e => updatePorts("portState", e.target.value === "enable")}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const portState = defaultStateTypes[key];
                        const changed = isEdited.portState != null;

                        return (
                          <FormControlLabel
                            disabled={!isEditable}
                            key={portState.key}
                            value={portState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={portState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }
              </StyledBox>

              {/* Port Type */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Type
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    row
                    disabled
                    value={selectedProfile.portType}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultPortTypes).map((key) => {
                        const portType = defaultPortTypes[key];
                        const changed = isEdited.portType != null;

                        return (
                          <FormControlLabel
                            disabled={!isEditable}
                            key={portType.key}
                            value={portType.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={portType.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    disabled={!showEditGeneral}
                    value={selectedPort?.portType}
                    onChange={e => updatePorts("portType", e.target.value)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultPortTypes).map((key) => {
                        const portType = defaultPortTypes[key];
                        const changed = isEdited.portType != null;

                        return (
                          <FormControlLabel
                            disabled={!isEditable}
                            key={portType.key}
                            value={portType.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={portType.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }
              </StyledBox>

              {/* PVID */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  PVID
                </Typography>

                {
                  isProfileMode &&
                  <TextField
                    disabled
                    type="number"
                    value={selectedProfile?.PVID ?? ""}
                    sx={getInputStyles({
                      theme,
                      isInValid: false,
                      isEdited: false,
                      field: "PVID"
                    })}
                    helperText="(1~4094)"
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                  />
                }

                {
                  !isProfileMode &&
                  <TextField
                    disabled={!isEditable || !showEditGeneral}
                    type="number"
                    value={selectedPort?.PVID ?? ""}
                    helperText="(1~4094)"
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                    onChange={e => updatePorts("PVID", e.target.value)}
                    onBlur={e => {
                      const num = Number(e.target.value);
                      if (!isNaN(num)) updatePorts("PVID", num);
                    }}
                    sx={getInputStyles({
                      theme,
                      isInValid: !checkPvid(selectedPort?.PVID?.toString().trim()),
                      isEdited,
                      field: "PVID"
                    })}
                  />
                }

              </StyledBox>

              {
                !isProfileMode &&
                vlanIdsCount.number > 128 &&
                <>
                  <StyledBox sx={{ marginTop: "0" }}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.primary.redPrimary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Limit reached: Maximum 128 VLANs allowed.
                    </Typography>

                  </StyledBox>
                </>
              }

              {/* Allow VLAN */}
              {
                isProfileMode &&
                selectedPort?.portType === defaultPortTypes.trunk.value &&
                <StyledBox>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    Allow VLAN
                  </Typography>

                  <TextField
                    disabled
                    type="text"
                    placeholder="e.g. 1,4-7"
                    helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                    value={selectedProfile?.allowVlan}
                    sx={getInputStyles({
                      theme,
                      isInValid: false,
                      isEdited: false,
                      field: "allowVlan"
                    })}
                  />

                </StyledBox>
              }

              {
                !isProfileMode &&
                selectedPort?.portType === defaultPortTypes.trunk.value &&
                <>
                  <StyledBox>
                    <Typography className={mainStyle["collapse-typography"]}>
                      Allow VLANs
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        disabled={!isEditable || !showEditGeneral}
                        type="text"
                        value={selectedPort?.allowVlan}
                        helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                        FormHelperTextProps={{
                          sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                        }}
                        onChange={e => updatePorts("allowVlan", e.target.value)}
                        sx={getInputStyles({
                          theme,
                          isInValid: isVlanInvalid,
                          isEdited,
                          field: "allowVlan"
                        })}
                        placeholder="e.g. 1, 4-7"
                      />
                    </Box>

                  </StyledBox>
                  {
                    vlanIdsCount.number > 128 &&
                    <>
                      <StyledBox sx={{ marginTop: "0" }}>
                        <Typography
                          className={mainStyle["collapse-typography"]}
                          sx={{ alignSelf: "flex-start", pt: "6px" }}
                        >
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.palette.primary.redPrimary,
                            fontSize: "0.75rem",
                          }}
                        >
                          Limit reached: Maximum 128 VLANs allowed.
                        </Typography>

                      </StyledBox>
                    </>
                  }
                </>
              }

              {/* PoE State */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  PoE State
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    row
                    disabled
                    value={selectedProfile?.poeState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >

                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const poeState = defaultStateTypes[key];
                        const changed = isEdited.poeState != null;

                        return (
                          <FormControlLabel
                            disabled
                            key={poeState.key}
                            value={poeState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={poeState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    disabled={!showEditGeneral}
                    value={selectedPort?.poeState ? "enable" : "disable"}
                    onChange={e => updatePorts("poeState", e.target.value === "enable")}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const poeState = defaultStateTypes[key];
                        const changed = isEdited.poeState != null;

                        return (
                          <FormControlLabel
                            disabled={!isEditable}
                            key={poeState.key}
                            value={poeState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={poeState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }

              </StyledBox>

              {/* Copper Link Speed */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: 2,
                }}
              >

                {
                  isCopper &&
                  linkSpeeds.copperList.length > 0 &&
                  <>
                    <StyledBox>
                      <Typography className={mainStyle["collapse-typography"]}>
                        Copper Link Speed
                      </Typography>

                      {
                        isProfileMode &&
                        <MenuSelect
                          disabled
                          value={selectedProfile?.speed}
                          options={linkSpeeds.copperList}
                          width="300px"
                        />
                      }

                      {
                        !isProfileMode &&
                        <MenuSelect
                          disabled={!isEditable || !showEditGeneral}
                          value={selectedPort?.speed || ""}
                          onChange={opt => {
                            updatePorts("speed", opt.value);

                            setLinkSpeeds(prevLinkSpeeds => {
                              return {
                                ...prevLinkSpeeds,
                                copperList: prevLinkSpeeds.copperList.map((speed, index) => {
                                  return {
                                    ...speed,
                                    isSelected: speed.value === opt.value ?? index === 0
                                  }
                                })
                              }
                            });
                          }}
                          options={linkSpeeds.copperList}
                          width="300px"
                          isEditedField={isEdited.speed}
                        />
                      }
                    </StyledBox>

                  </>
                }

                {/* Fiber Link Speed */}
                {
                  isFiber &&
                  linkSpeeds.fiberList.length > 0 &&
                  <StyledBox>
                    <Typography className={mainStyle["collapse-typography"]}>
                      Fiber Link Speed
                    </Typography>

                    {
                      isProfileMode &&
                      <MenuSelect
                        disabled
                        value={selectedProfile?.speed}
                        options={linkSpeeds.fiberList}
                        width="300px"
                      />
                    }

                    {
                      !isProfileMode &&
                      <MenuSelect
                        disabled={!isEditable || !showEditGeneral}
                        value={selectedPort?.comboSfpSpeed || ""}
                        onChange={opt => {
                          updatePorts("comboSfpSpeed", opt.value);

                          setLinkSpeeds(prevLinkSpeeds => {
                            return {
                              ...prevLinkSpeeds,
                              fiberList: prevLinkSpeeds.fiberList.map((comboSfpSpeed, index) => {
                                return {
                                  ...comboSfpSpeed,
                                  isSelected: comboSfpSpeed.value === opt.value ?? index === 0
                                }
                              })
                            }
                          });
                        }}
                        options={linkSpeeds.fiberList}
                        width="300px"
                        isEditedField={isEdited.comboSfpSpeed}
                      />
                    }
                  </StyledBox>

                }
              </Box>

              {/* DDP */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  DDP
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    row
                    disabled
                    value={selectedProfile?.ddpState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >

                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const ddpState = defaultStateTypes[key];
                        const changed = isEdited.ddpState != null;

                        return (
                          <FormControlLabel
                            disabled
                            key={ddpState.key}
                            value={ddpState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={ddpState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    disabled={!showEditGeneral}
                    value={selectedPort?.ddpState ? "enable" : "disable"}
                    onChange={e => updatePorts("ddpState", e.target.value === "enable")}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const ddpState = defaultStateTypes[key];
                        const changed = isEdited.ddpState != null;

                        return (
                          <FormControlLabel
                            disabled={!isEditable}
                            key={ddpState.key}
                            value={ddpState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={ddpState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }

              </StyledBox>

              {/* LBD */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  LBD
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    row
                    disabled
                    value={selectedProfile?.lbdState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >

                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const lbdState = defaultStateTypes[key];
                        const changed = isEdited.lbdState != null;

                        return (
                          <FormControlLabel
                            disabled
                            key={lbdState.key}
                            value={lbdState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={lbdState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    disabled={!showEditGeneral}
                    value={selectedPort?.lbdState ? "enable" : "disable"}
                    onChange={e => updatePorts("lbdState", e.target.value === "enable")}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {Object.keys(defaultStateTypes).map((key) => {
                      const lbdState = defaultStateTypes[key];
                      const changed = isEdited.lbdState != null;

                      return (
                        <FormControlLabel
                          disabled={!isEditable}
                          key={lbdState.key}
                          value={lbdState.value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={
                                changed ? <CheckChangedIcon /> : <CheckedIcon />
                              }
                            />
                          }
                          label={lbdState.label}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }

              </StyledBox>
            </>
          )}
        </Box>

        <Box
          sx={{
            paddingTop: "20px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
            onClick={onCancel}
          >
            Cancel
          </OutlineButton>
          <SolidButton
            width="100px"
            height="32px"
            disabled={isFormInvalid()}
            onClick={onApply}
          >
            Apply
          </SolidButton>
        </Box>
      </ModalContainer>
    </>
  );
};

export default EditModalSwitch;
